// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const product = this.closest('product-component');
    const { signal } = this.#abortController;

    if (this.querySelector('[data-design-style-selector]')) {
      this.addEventListener('change', this.#handleDesignStyleChange, { signal });
      product?.addEventListener('change', this.#handleNativeOptionChange, { signal });
      queueMicrotask(this.#syncDesignStyleSelection);
    }

    if (!this.querySelector('[data-pack-validation]')) return;

    const form = this.refs.textInput?.form;

    form?.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    product?.addEventListener('change', this.#handleVariantChange, { signal });
    this.#updatePackGuidance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  handleInput() {
    this.#updateCharacterCount();
    if (this.querySelector('[data-pack-validation]')) this.#validatePlayerNames(false);
  }

  #handleVariantChange = () => {
    queueMicrotask(() => {
      this.#updatePackGuidance();
      this.#validatePlayerNames(false);
    });
  };

  #handleDesignStyleChange = (event) => {
    const visualOption = event.target;
    if (!(visualOption instanceof HTMLInputElement) || !visualOption.matches('[data-design-style-value]')) return;

    const selector = this.querySelector('[data-design-style-selector]');
    const product = this.closest('product-component');
    const nativeOptionName = selector?.dataset.nativeOptionName?.trim().toLowerCase();
    const nativeOptions = product?.querySelectorAll('variant-picker input[data-option-name]') ?? [];
    const nativeOption = Array.from(nativeOptions).find(
      (option) =>
        option.dataset.optionName?.trim().toLowerCase() === nativeOptionName &&
        option.value === visualOption.dataset.designStyleValue
    );

    if (!(nativeOption instanceof HTMLInputElement)) {
      this.#syncDesignStyleSelection();
      return;
    }

    if (!nativeOption.checked) {
      nativeOption.checked = true;
      nativeOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #handleNativeOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.closest('variant-picker')) return;
    queueMicrotask(this.#syncDesignStyleSelection);
  };

  #syncDesignStyleSelection = () => {
    const selector = this.querySelector('[data-design-style-selector]');
    const product = this.closest('product-component');
    const nativeOptionName = selector?.dataset.nativeOptionName?.trim().toLowerCase();
    const selectedNativeOption = Array.from(
      product?.querySelectorAll('variant-picker input[data-option-name]:checked') ?? []
    ).find((option) => option.dataset.optionName?.trim().toLowerCase() === nativeOptionName);

    if (!(selectedNativeOption instanceof HTMLInputElement)) return;

    this.querySelectorAll('[data-design-style-value]').forEach((visualOption) => {
      if (visualOption instanceof HTMLInputElement) {
        visualOption.checked = visualOption.dataset.designStyleValue === selectedNativeOption.value;
      }
    });
  };

  #handleSubmit = (event) => {
    if (this.#validatePlayerNames(true)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.refs.textInput?.focus();
  };

  #getSelectedPackSize() {
    const product = this.closest('product-component');
    const selectedOptions = product?.querySelectorAll('variant-picker input[type="radio"]:checked') ?? [];

    for (const option of selectedOptions) {
      const match = option.value.match(/^(4|8|12)\b/i);
      if (match) return Number.parseInt(match[1], 10);
    }

    return null;
  }

  #updatePackGuidance() {
    const guidance = this.refs.packGuidance;
    if (!(guidance instanceof HTMLElement)) return;

    const baseText = guidance.dataset.baseText ?? '';
    const packSize = this.#getSelectedPackSize();
    guidance.textContent = packSize ? `${baseText} — ${packSize} names required.` : baseText;
  }

  #validatePlayerNames(showError) {
    const input = this.refs.textInput;
    const error = this.refs.validationError;
    if (!(input instanceof HTMLTextAreaElement) || !(error instanceof HTMLElement)) return true;

    const packSize = this.#getSelectedPackSize();
    const lineCount = input.value.split(/\r?\n/).filter((line) => line.trim().length > 0).length;
    const isValid = packSize === null || lineCount === packSize;
    const message = isValid ? '' : `Please enter exactly ${packSize} player names, one per line.`;

    input.setCustomValidity(message);
    input.toggleAttribute('aria-invalid', !isValid);

    if (isValid) {
      input.removeAttribute('aria-describedby');
      error.textContent = '';
      error.hidden = true;
    } else {
      input.setAttribute('aria-describedby', error.id);
      error.textContent = showError ? message : '';
      error.hidden = !showError;
    }

    return isValid;
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
